/**
 * Per-customer display currency (ISO 4217). Presentation-only: customer data
 * carries no currency context and is never converted — each tenant uploads
 * values in their own currency, we only label them. Internally
 * `kpi_unit === 'euro'` still means "monetary" (vs 'unit' = counts); the
 * symbol shown for monetary values comes from the customer's currency.
 *
 * Curated for the EU ICP; extend when new markets need it. Symbol position
 * follows each call site's existing convention (prefix/suffix) — deferred
 * until non-EU markets require per-currency positioning.
 */
export type SupportedCurrency = {
  code: string;
  symbol: string;
  /** Which side of the number the symbol goes on ('20,000€' vs '$20,000') */
  position: 'prefix' | 'suffix';
};

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = [
  // EU / EEA — continental convention: symbol after the number
  { code: 'EUR', symbol: '€', position: 'suffix' },
  { code: 'GBP', symbol: '£', position: 'prefix' },
  { code: 'SEK', symbol: 'kr', position: 'suffix' },
  { code: 'DKK', symbol: 'kr', position: 'suffix' },
  { code: 'NOK', symbol: 'kr', position: 'suffix' },
  { code: 'PLN', symbol: 'zł', position: 'suffix' },
  { code: 'CZK', symbol: 'Kč', position: 'suffix' },
  { code: 'HUF', symbol: 'Ft', position: 'suffix' },
  { code: 'RON', symbol: 'lei', position: 'suffix' },
  { code: 'BGN', symbol: 'лв', position: 'suffix' },
  { code: 'CHF', symbol: 'CHF', position: 'prefix' },
  // Balkans (non-euro; Croatia/Slovenia/Montenegro/Kosovo use EUR)
  { code: 'RSD', symbol: 'дин.', position: 'suffix' },
  { code: 'BAM', symbol: 'KM', position: 'suffix' },
  { code: 'MKD', symbol: 'ден', position: 'suffix' },
  { code: 'ALL', symbol: 'L', position: 'suffix' },
  // North America — symbol before the number
  { code: 'USD', symbol: '$', position: 'prefix' },
  { code: 'CAD', symbol: 'CA$', position: 'prefix' },
  // Post-Soviet (Baltics use EUR)
  { code: 'UAH', symbol: '₴', position: 'suffix' },
  { code: 'MDL', symbol: 'L', position: 'suffix' },
  { code: 'GEL', symbol: '₾', position: 'suffix' },
  { code: 'AMD', symbol: '֏', position: 'suffix' },
  { code: 'AZN', symbol: '₼', position: 'suffix' },
  { code: 'KZT', symbol: '₸', position: 'suffix' },
  { code: 'UZS', symbol: 'soʻm', position: 'suffix' },
  { code: 'KGS', symbol: 'с', position: 'suffix' },
  { code: 'TJS', symbol: 'SM', position: 'suffix' },
  { code: 'TMT', symbol: 'm', position: 'suffix' },
  { code: 'BYN', symbol: 'Br', position: 'suffix' },
  { code: 'RUB', symbol: '₽', position: 'suffix' },
];

export const DEFAULT_CURRENCY_CODE = 'EUR';

/**
 * Symbol for a supported currency code. An UNKNOWN code deliberately falls
 * back to the raw code itself ('ZZ1 20,000').
 */
export const getCurrencySymbol = (code: string | null | undefined): string =>
  SUPPORTED_CURRENCIES.find((c) => c.code === code)?.symbol ?? code ?? '?';

const currencyDisplayNames = new Intl.DisplayNames(['en'], {
  type: 'currency',
});

// Single-glyph symbols attach directly to the number (45K€, $45K);
// letter-based symbols (kr, zł, CHF...) need a space — '45.00Kkr' is unreadable
const GLYPH_SYMBOLS = new Set(['€', '£', '$']);

/**
 * The prefix/suffix strings to place around a number for the given currency
 * CODE (symbols are not unique — kr, L — so the code is the lookup key), with
 * position and spacing resolved. For call sites that render the number and
 * the symbol in separate elements.
 */
export const getCurrencyAffixes = (
  code: string | null | undefined,
): { prefix: string; suffix: string } => {
  const currency = SUPPORTED_CURRENCIES.find((c) => c.code === code);
  // Unknown code: show the raw code, spaced, as an explicit "unresolved
  // currency" marker — never a plausible-but-wrong symbol like '€'.
  // Nullish degrades to '?' (same terminal marker as getCurrencySymbol).
  const symbol = currency?.symbol ?? code ?? '?';
  const position = currency?.position ?? 'suffix';
  const separator = GLYPH_SYMBOLS.has(symbol) ? '' : ' ';

  if (position === 'prefix') {
    return { prefix: `${symbol}${separator}`, suffix: '' };
  }
  return { prefix: '', suffix: `${separator}${symbol}` };
};

/**
 * Attach the currency symbol to a formatted number, using the currency's
 * conventional side and spacing: '20,000€', '45.00K kr', '$20,000', 'CHF 100'.
 */
export const attachCurrency = (value: string, code: string): string => {
  const { prefix, suffix } = getCurrencyAffixes(code);
  return `${prefix}${value}${suffix}`;
};

/**
 * Currency display name, e.g. 'SEK' => 'Swedish Krona', 'EUR' => 'Euro'.
 * Falls back to the raw code (Intl throws on malformed codes).
 */
export const getCurrencyName = (code: string): string => {
  try {
    return currencyDisplayNames.of(code) ?? code;
  } catch {
    return code;
  }
};

/**
 * Human label for dropdowns, e.g. 'SEK' => 'Swedish Krona (kr)'.
 */
export const getCurrencyLabel = (code: string): string =>
  `${getCurrencyName(code)} (${getCurrencySymbol(code)})`;
